import { Slide, ToastContainer, Zoom, toast } from "react-toastify";
import { Alert } from "types/alert";
import { FieldsBible } from "types/holy-books";
import { Geofence } from "types/geofence";
import { useStore } from "hooks/use-store";
import { useEffect } from "react";
import { formatAlert } from "./utils";
import { SystemStatusChanges } from "types/system-status";
import { useTranslation } from "react-i18next";
import { Button } from "components/ui/button";
import { opacity } from "utils/canvas";
import { Color } from "utils/theme";
import useMqttApi from "hooks/use-mqtt-api";
import { RequestTopics } from "services/topics";
import { InfoIcon, OctagonAlert, TriangleAlert, XIcon } from "lucide-react";

const TOAST_TIMEOUT = 5_000; // 5 seconds
// Alert toasts without an acknowledgement requirement dismiss on their own
const ALERT_TIMEOUT = 15_000; // 15 seconds
const CRITICAL_ALERT_TIMEOUT = 15_000; // 15 seconds
const STATUS_TIMEOUT = 30_000; // 30 seconds
const TOAST_LIMIT = 1;

type AlertToastProps = {
  severity: string;
  text: string;
  closeToast?: () => void;
};

const AlertToast = ({ severity, text, closeToast }: AlertToastProps) => {
  const styleOptions = {
    ["critical"]: {
      backgroundColor: opacity(Color.Rose[800], 0.95),
      borderColor: Color.Rose[600],
    },
    ["warning"]: {
      backgroundColor: opacity(Color.Amber[700], 0.95),
      borderColor: Color.Amber[600],
    },
    ["info"]: {
      backgroundColor: opacity(Color.Blue[900], 0.95),
      borderColor: Color.Blue[700],
    },
  };

  const iconOptions = {
    ["critical"]: OctagonAlert,
    ["warning"]: TriangleAlert,
    ["info"]: InfoIcon,
  };

  const style = styleOptions[severity] || styleOptions["info"];
  const Icon = iconOptions[severity] || iconOptions["info"];

  return (
    <div
      className="flex items-center border p-6 space-x-4 rounded-lg"
      style={style}
    >
      <div className="flex space-x-4 flex-1">
        <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white shrink-0" />
        <p className="text-xl sm:text-2xl font-medium flex-1">{text}</p>
      </div>
      <Button
        className="w-12 h-12 text-white border-0"
        onClick={closeToast}
        style={{
          background: opacity("#ffffff", 0.1),
        }}
        size="icon"
      >
        <XIcon className="size-6 text-2xl" />
      </Button>
    </div>
  );
};

// Full-screen flashing toast for critical alerts. Dismisses itself after
// CRITICAL_ALERT_TIMEOUT, or on tap (closeOnClick on the toast).
const CriticalAlertToast = ({ text }: { text: string }) => {
  return (
    <div
      className="border rounded-lg overflow-hidden absolute top-0 left-0 right-0 bottom-0"
      style={{ borderColor: Color.Rose[600] }}
    >
      <div
        className="absolute top-0 left-0 right-0 bottom-0"
        style={{ backgroundColor: opacity(Color.Rose[900], 0.95) }}
      />
      <div
        className="absolute top-0 left-0 right-0 bottom-0 animate-flash"
        style={{ backgroundColor: opacity(Color.Rose[700], 0.95) }}
      />
      <div className="relative h-full w-full p-6 flex flex-col items-center justify-center space-y-6 md:space-y-12">
        <OctagonAlert className="w-16 h-16 md:w-28 md:h-28 text-white shrink-0" />
        <p className="text-3xl md:text-5xl font-semibold uppercase text-center">
          {text}
        </p>
      </div>
    </div>
  );
};

type AlertAcknowledgementToastProps = {
  severity: string;
  text: string;
  alertId: string;
  closeToast?: () => void;
};

const AlertAcknowledgementToast = ({
  severity,
  text,
  alertId,
  closeToast,
}: AlertAcknowledgementToastProps) => {
  const { fetch: acknowledgeAlert } = useMqttApi({
    requestTopic: RequestTopics.AcknowledgeAlert,
    enabled: false,
  });

  const styleOptions = {
    ["critical"]: {
      backgroundColor: opacity(Color.Rose[800], 0.95),
      borderColor: Color.Rose[600],
    },
    ["warning"]: {
      backgroundColor: opacity(Color.Amber[700], 0.95),
      borderColor: Color.Amber[600],
    },
    ["info"]: {
      backgroundColor: opacity(Color.Blue[900], 0.95),
      borderColor: Color.Blue[700],
    },
  };

  const iconOptions = {
    ["critical"]: OctagonAlert,
    ["warning"]: TriangleAlert,
    ["info"]: InfoIcon,
  };

  const style = styleOptions[severity] || styleOptions["info"];
  const Icon = iconOptions[severity] || iconOptions["info"];
  const isCritical = severity === "critical";

  const onConfirm = () => {
    acknowledgeAlert({
      alert_id: alertId,
      timestamp: new Date(),
    });
    if (closeToast) {
      closeToast();
    }
  };

  return (
    <div
      className="p-6 justify-center border rounded-lg h-full w-full flex flex-col items-center space-y-6 absolute top-0 left-0 right-0 bottom-0 overflow-hidden"
      style={
        isCritical
          ? { borderColor: style.borderColor }
          : style
      }
    >
      {isCritical && (
        <>
          <div
            className="absolute top-0 left-0 right-0 bottom-0"
            style={{ backgroundColor: opacity(Color.Rose[900], 0.95) }}
          />
          <div
            className="absolute top-0 left-0 right-0 bottom-0 animate-flash"
            style={{ backgroundColor: opacity(Color.Rose[700], 0.95) }}
          />
        </>
      )}
      <div className="relative space-y-6 md:space-y-12 flex flex-col items-center justify-center flex-1 w-full">
        <Icon className="w-16 h-16 md:w-28 md:h-28 text-white shrink-0" />
        <p className="text-3xl md:text-5xl font-semibold uppercase text-center">
          {text}
        </p>
      </div>
      <button
        className="relative shrink-0 w-full h-20 md:h-32 text-white text-4xl rounded-xl tracking-wider font-semibold"
        style={{
          backgroundColor: opacity("#ffffff", 0.2),
        }}
        onClick={onConfirm}
        onPointerDown={(e) => {
          e.currentTarget.style.opacity = "0.6";
        }}
        onPointerUp={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
        onPointerLeave={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
        onPointerEnter={(e) => {
          e.currentTarget.style.opacity = "0.8";
        }}
      >
        Confirm
      </button>
    </div>
  );
};

const triggerAlertToast = (
  alert: Alert,
  fieldsBible: FieldsBible | null,
  geofences: Geofence[]
) => {
  const formattedAlert = formatAlert(alert, fieldsBible, geofences);

  // If the alert requires acknowledgement, display a special toast
  if (alert.info?.rule?.notifications?.helm?.requires_acknowledgement) {
    toast(
      <AlertAcknowledgementToast
        alertId={alert.id}
        severity={formattedAlert.severity}
        text={formattedAlert.text}
      />,
      {
        autoClose: false,
        position: "top-left",
        className:
          "w-[calc(100vw-2rem)] h-[calc(100vh-2rem)] !max-h-none !left-0 !right-0 !bottom-0 !top-0 !p-0 !m-0 !bg-transparent",
        bodyClassName: "!p-0 !m-0 !bg-transparent",
        draggable: false,
        closeButton: false,
        closeOnClick: false,
        transition: Zoom,
      }
    );
    return;
  }

  // Critical alerts take over the whole screen with a flashing red background
  // to grab attention, but dismiss on their own — no acknowledgement needed.
  if (formattedAlert.severity === "critical") {
    toast(<CriticalAlertToast text={formattedAlert.text} />, {
      autoClose: CRITICAL_ALERT_TIMEOUT,
      position: "top-left",
      className:
        "w-[calc(100vw-2rem)] h-[calc(100vh-2rem)] !max-h-none !left-0 !right-0 !bottom-0 !top-0 !p-0 !m-0 !bg-transparent",
      bodyClassName: "!p-0 !m-0 !bg-transparent",
      draggable: false,
      closeButton: false,
      closeOnClick: true,
      transition: Zoom,
    });
    return;
  }

  toast(
    <AlertToast
      severity={formattedAlert.severity}
      text={formattedAlert.text}
    />,
    {
      autoClose: ALERT_TIMEOUT,
      position: "bottom-left",
      className:
        "w-[calc(100vw-2rem)] !left-0 !right-0 !bottom-0 !p-0 !m-0 !bg-transparent",
      bodyClassName: "!p-0 !m-0 !bg-transparent",
    }
  );
};

export const AlertContainer = () => {
  const { t: tStatusFields } = useTranslation("translation", {
    keyPrefix: "settings.system_status.fields",
  });
  const { t: tCommon } = useTranslation("translation", {
    keyPrefix: "common",
  });

  const config = useStore((state) => state.config);
  const alert = useStore((state) => state.alert);
  const systemStatusChanges = useStore((state) => state.systemStatusChanges);

  const triggerSystemStatusToast = (
    systemStatusChanges: SystemStatusChanges
  ) => {
    const isConnected = systemStatusChanges.connected;
    const name = systemStatusChanges.name;

    const text = isConnected
      ? `${tStatusFields(name)} ${tCommon("connected")}`
      : `${tStatusFields(name)} ${tCommon("disconnected")}`;

    if (isConnected) {
      toast.success(text, {
        autoClose: STATUS_TIMEOUT,
      });
    } else {
      toast.error(text, {
        autoClose: STATUS_TIMEOUT,
      });
    }
  };

  useEffect(() => {
    if (alert) {
      triggerAlertToast(
        alert,
        config?.holy_books?.fields_bible || null,
        config?.geofences || []
      );
    }
  }, [alert]);

  useEffect(() => {
    if (systemStatusChanges) {
      triggerSystemStatusToast(systemStatusChanges);
    }
  }, [systemStatusChanges]);

  return (
    <ToastContainer
      limit={TOAST_LIMIT}
      autoClose={TOAST_TIMEOUT}
      position="bottom-right"
      newestOnTop={true}
      rtl={false}
      pauseOnFocusLoss={false}
      pauseOnHover={false}
      closeButton={false}
      theme="dark"
      closeOnClick
      draggable
      hideProgressBar
      transition={Slide}
    />
  );
};
